import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  DisengageRoutingEmergencyOverrideRequest,
  DisengageRoutingEmergencyOverrideResponse,
  EngageRoutingEmergencyOverrideRequest,
  EngageRoutingEmergencyOverrideResponse,
  GetRoutingEmergencyOverrideResponse,
  ListRoutingDrPlansResponse,
  ListRoutingSwitchEventsResponse,
  RollbackRoutingSwitchRequest,
  RollbackRoutingSwitchResponse,
  RoutingConfigPreflightResponse,
  UpsertRoutingDrPlanRequest,
  VerifyRoutingDrPlanResponse,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import { TENANT_ROUTING_KEY } from './useTenantRouting.ts';
import {
  deleteRoutingDrPlan,
  disengageRoutingEmergencyOverride,
  engageRoutingEmergencyOverride,
  getRoutingEmergencyOverride,
  listRoutingDrPlans,
  listRoutingSwitchEvents,
  rollbackRoutingSwitch,
  runRoutingConfigPreflight,
  SWITCH_EVENTS_PAGE_SIZE,
  upsertRoutingDrPlan,
  verifyRoutingDrPlan,
} from './routingDr.ts';

const DR_PLANS_KEY = [...TENANT_ROUTING_KEY, 'dr-plans'] as const;
const SWITCH_EVENTS_KEY = [...TENANT_ROUTING_KEY, 'switch-events'] as const;

export function useRoutingDrPlans(): UseQueryResult<ListRoutingDrPlansResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: DR_PLANS_KEY,
    queryFn: ({ signal }) => listRoutingDrPlans(fetchImpl, signal),
  });
}

export function useRoutingSwitchEvents(): UseQueryResult<ListRoutingSwitchEventsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...SWITCH_EVENTS_KEY, SWITCH_EVENTS_PAGE_SIZE],
    queryFn: ({ signal }) => listRoutingSwitchEvents(SWITCH_EVENTS_PAGE_SIZE, fetchImpl, signal),
  });
}

export function useUpsertRoutingDrPlan(): UseMutationResult<
  void,
  Error,
  { configId: string } & UpsertRoutingDrPlanRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, ...body }) => {
      await upsertRoutingDrPlan(configId, body, fetchImpl);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DR_PLANS_KEY });
    },
  });
}

export function useDeleteRoutingDrPlan(): UseMutationResult<void, Error, { configId: string }> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId }) => {
      await deleteRoutingDrPlan(configId, fetchImpl);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DR_PLANS_KEY });
    },
  });
}

/**
 * The read-only preflight. Deliberately a mutation hook despite writing
 * nothing: it is an operator-triggered action with a large response that must
 * not be re-fetched behind the operator's back while they read it.
 */
export function useRunRoutingConfigPreflight(): UseMutationResult<
  RoutingConfigPreflightResponse,
  Error,
  { configId: string; windowHours?: number }
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: ({ configId, windowHours }) =>
      runRoutingConfigPreflight(
        configId,
        windowHours === undefined ? {} : { window_hours: windowHours },
        fetchImpl,
      ),
  });
}

/** Same evaluation, recorded on the plan — so the plan list's staleness moves. */
export function useVerifyRoutingDrPlan(): UseMutationResult<
  VerifyRoutingDrPlanResponse,
  Error,
  { configId: string; windowHours?: number }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ configId, windowHours }) =>
      verifyRoutingDrPlan(
        configId,
        windowHours === undefined ? {} : { window_hours: windowHours },
        fetchImpl,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DR_PLANS_KEY });
    },
  });
}

/**
 * Rollback invalidates the whole tenant-routing tree, not just the event list:
 * it moves the global assignment, so the config library's `is_global_default`
 * and every assignment read are stale the moment it lands.
 */
export function useRollbackRoutingSwitch(): UseMutationResult<
  RollbackRoutingSwitchResponse,
  Error,
  RollbackRoutingSwitchRequest & { readonly expected_event_id: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => rollbackRoutingSwitch(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

// ── Emergency override (#1422) ──────────────────────────────────────────────

export const EMERGENCY_OVERRIDE_KEY = [...TENANT_ROUTING_KEY, 'emergency-override'] as const;

/**
 * Polled rather than read once. The override is fleet-wide state that another
 * operator can change mid-incident, and a panel that quietly showed a stale
 * "not engaged" would invite a second engage on top of a first.
 */
export function useRoutingEmergencyOverride(): UseQueryResult<GetRoutingEmergencyOverrideResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: EMERGENCY_OVERRIDE_KEY,
    queryFn: ({ signal }) => getRoutingEmergencyOverride(fetchImpl, signal),
    refetchInterval: 30_000,
  });
}

/**
 * Both directions invalidate the whole tenant-routing tree, like rollback and
 * for a stronger version of the same reason: engaging changes the effective
 * route of *every* org, so every routing read on screen is stale the moment it
 * lands.
 */
export function useEngageRoutingEmergencyOverride(): UseMutationResult<
  EngageRoutingEmergencyOverrideResponse,
  Error,
  EngageRoutingEmergencyOverrideRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => engageRoutingEmergencyOverride(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useDisengageRoutingEmergencyOverride(): UseMutationResult<
  DisengageRoutingEmergencyOverrideResponse,
  Error,
  DisengageRoutingEmergencyOverrideRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => disengageRoutingEmergencyOverride(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}
